import React, { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { UncontrolledTooltip } from "reactstrap";
import truncate from "lodash/truncate";

import Toast from "shared/toast";
import Header, { HeaderContainer, HeaderTitle, SecondaryTitle } from "shared/react_components/Header";
import Centered from "shared/react_components/Centered";
import IconButton from "shared/react_components/IconButton";
import Loader from "shared/react_components/Loader";
import AudioPlayer, { EventsTrack } from "shared/react_components/AudioPlayer";
import ShareButton from "shared/react_components/ShareButton";
import {
  callDetailsHeader, notFound, audioPlayerContainer, panesContainer, mainPane, transcriptPane, dataPaneVisible, dataPaneHidden
} from "calls/styles/CallDetails.module.scss";

import { GetCall } from "./graphql/Calls.graphql";
import CallDataPane from "./CallDataPane";
import CommentsPane from "./CommentsPane";
import ScoresPane from "./ScoresPane";
import CallDetailsNavColumn from "./CallDetailsNavColumn";
import TranscriptPane from "./TranscriptPane";
import { useRemoveCommentUrlParams } from "./comments/removeCommentUrlParams";

const CallDetails = ({ callsReportUrl, enableCallCommenting, enableCallScoring, currentPane, showHeader, showDataPaneHeader, containerClassName }) => {
  const history = useHistory();
  const { cuid } = useParams();
  const removeCommentUrlParams = useRemoveCommentUrlParams();

  const [mostRecentCuid, setMostRecentCuid] = useState(null);
  const [currentAudioPosition, setCurrentAudioPosition] = useState();
  const [audioJumpPosition, setAudioJumpPosition] = useState();
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [commentReferences, setCommentReferences] = useState({});
  const [scrollToNewComment, setScrollToNewComment] = useState(false);
  const [audioPlayerPaused, setAudioPlayerPaused] = useState(true);

  const downloadButtonRef = useRef(null);

  useEffect(() => {
    // We need to clear some settings so they don't persist when the CUID changes (e.g. when selected call in call list view changes)
    // If mostRecentCuid is not null that means this component could be re-rendering with a different cuid.
    if (mostRecentCuid !== null && cuid !== mostRecentCuid) {
      setCommentReferences({});
      setTranscriptExpanded(false);
      setScrollToNewComment(false);
      setAudioPlayerPaused(true);
      removeCommentUrlParams();
      setCurrentAudioPosition(undefined);
    }
    setMostRecentCuid(cuid);
  }, [cuid, mostRecentCuid]);

  const { data, loading, error } = useQuery(GetCall, {
    variables: { id: cuid }
  });

  const { pathname } = useLocation();
  const shareURL = window.location.href.replace(pathname, `/${cuid}`);

  if (loading) {
    return (
      <Centered height="500px">
        <Loader />
      </Centered>
    );
  }

  if (error) {
    if (
      error.graphQLErrors.length &&
      error.graphQLErrors[0].extensions &&
      error.graphQLErrors[0].extensions.code === "NOT_FOUND"
    ) {
      return (
        <Centered height="500px">
          <p className={notFound}>Call not found.</p>
        </Centered>
      );
    } else {
      Toast.error({
        title: "Couldn't load Call",
        message:
          "An error occurred while trying to load the call detail page. Please try again."
      });

      return null;
    }
  }

  const { organization, call } = data;
  // Treat an empty transcript as a non-existent transcript
  const transcriptEvents = call.transcript && call.transcript.events && call.transcript.events.length > 0 ? call.transcript.events : null;
  const leftAlignNavColumn = !transcriptEvents && (enableCallCommenting || enableCallScoring);

  const onPause = () => setAudioPlayerPaused(true);
  const onPlay = () => setAudioPlayerPaused(false);

  const onAudioJumpPositionCallback = () => {
    // Don't allow jumping audio if we don't have audio to play
    if (call.recordingUrl) {
      return pos => setAudioJumpPosition(pos);
    }
  };

  const focusOnAddingNewComment = () => {
    if (transcriptExpanded) {
      setTranscriptExpanded(false);
    }
    if (currentPane !== "comments") {
      history.push(`/${cuid}/comments`);
    }
    setScrollToNewComment(true);
  };

  const formatTranscriptReference = transcriptEvent => {
    if (transcriptEvent) {
      const { text, startTime, speaker } = transcriptEvent;
      return { text: truncate(text, { length: 1000 }), startTime, speaker };
    }
  };

  const addTranscriptCommentReferenceCallback = () => {
    // Don't allow adding comment transcript references if commenting is not enabled
    if (enableCallCommenting) {
      return (transcriptEvent) => {
        const callRecordingRefAttributes = call.recordingUrl ? { callRecordingRef: { startTime: transcriptEvent.startTime } } : {};
        setCommentReferences({
          ...callRecordingRefAttributes,
          transcriptRef: formatTranscriptReference(transcriptEvent)
        });
        focusOnAddingNewComment();
      };
    }
  };

  const findMatchingTranscriptEventReference = () => {
    if (transcriptEvents) {
      const transcriptEvent = transcriptEvents.find(({ startTime, endTime }) => startTime <= currentAudioPosition && endTime >= currentAudioPosition);
      return formatTranscriptReference(transcriptEvent);
    }
  };

  const addCallRecordingCommentReferenceCallback = () => {
    // Don't allow adding comment recording references if commenting is not enabled
    if (enableCallCommenting && call.recordingUrl) {
      return () => {
        setCommentReferences({
          callRecordingRef: { startTime: currentAudioPosition },
          transcriptRef: findMatchingTranscriptEventReference()
        });
        focusOnAddingNewComment();
      };
    }
  };

  const mainCallDetailsPane = () => {
    if (currentPane === "comments" && enableCallCommenting) {
      return (
        <CommentsPane
          cuid={cuid}
          onAudioJumpPosition={onAudioJumpPositionCallback()}
          commentReferences={commentReferences}
          removeCommentReferences={() => setCommentReferences({})}
          scrollToNewComment={scrollToNewComment}
          onScrolledToNewComment={() => setScrollToNewComment(false)}
          onAddCallReference={addCallRecordingCommentReferenceCallback()}
        />
      );
    } else if (currentPane === "scores" && enableCallScoring) {
      return (
        <ScoresPane
          scoreDetails={call.scoreDetails}
        />
      );
    } else {
      return (
        <CallDataPane
          call={call}
          organization={organization}
          showHeader={showDataPaneHeader}
        />
      );
    }
  };

  return (
    <>
      {showHeader && (
        <Header className={callDetailsHeader}>
          <div>
            <SecondaryTitle
              component="a"
              href={
                document.referrer.includes(callsReportUrl)
                  ? document.referrer
                  : callsReportUrl
              }
            >
              Calls Report
            </SecondaryTitle>
            <HeaderTitle>Call Details</HeaderTitle>
          </div>
          <div className="d-flex">
            <ShareButton shareURL={shareURL}/>
            {call.recordingUrl && (
              <React.Fragment>
                <div ref={downloadButtonRef}>
                  <IconButton icon="get_app" href={call.recordingUrl} download/>
                </div>
                <UncontrolledTooltip target={downloadButtonRef} placement="left" delay={1000}>
                  Download call recording
                </UncontrolledTooltip>
              </React.Fragment>
            )}
          </div>
        </Header>
      )}

      {call.recordingUrl && (
        <HeaderContainer className={audioPlayerContainer}>
          <AudioPlayer
            src={call.recordingUrl}
            showPlaybackRate
            showQuickNav
            remotePaused={audioPlayerPaused}
            onPause={onPause}
            onPlay={onPlay}
            onPositionChange={pos => setCurrentAudioPosition(pos)}
            audioJumpPosition={audioJumpPosition}
            onAudioPositionJumped={() => setAudioJumpPosition(null)}
            onAddComment={addCallRecordingCommentReferenceCallback()}
          >
            {props => transcriptEvents && <EventsTrack events={transcriptEvents} {...props} />}
          </AudioPlayer>
        </HeaderContainer>
      )}

      <div className={classNames(panesContainer, leftAlignNavColumn ? "pl-0" : "", containerClassName)} data-test-id="panes-container">
        {transcriptEvents &&
          <TranscriptPane
            className={transcriptPane}
            events={transcriptEvents}
            currentAudioPosition={currentAudioPosition}
            onAudioJumpPosition={onAudioJumpPositionCallback()}
            enableAutoScroll={!!call.recordingUrl}
            onAddComment={addTranscriptCommentReferenceCallback()}
          />
        }

        {(transcriptEvents || enableCallCommenting || enableCallScoring) &&
          <CallDetailsNavColumn
            cuid={cuid}
            currentPane={currentPane}
            enableCallCommenting={enableCallCommenting}
            enableCallScoring={enableCallScoring}
            transcriptPresent={!!transcriptEvents}
            transcriptExpanded={transcriptExpanded}
            onTranscriptExpandedChange={expanded => setTranscriptExpanded(expanded)}
          />
        }

        <div className={classNames(transcriptExpanded ? dataPaneHidden : dataPaneVisible, mainPane)}>
          {mainCallDetailsPane()}
        </div>
      </div>
    </>
  );
};

CallDetails.propTypes = {
  currentPane: PropTypes.oneOf(["calls", "comments", "scores"]).isRequired,
  callsReportUrl: PropTypes.string.isRequired,
  enableCallCommenting: PropTypes.bool,
  enableCallScoring: PropTypes.bool,
  showHeader: PropTypes.bool,
  showDataPaneHeader: PropTypes.bool,
  containerClassName: PropTypes.string
};

CallDetails.defaultProps = {
  enableCallCommenting: false,
  enableCallScoring: false,
  showHeader: true,
  showDataPaneHeader: false,
  containerClassName: ""
};

export default CallDetails;
